import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import {
  useAPIGetPengembanganKompetensi,
  useAPIGetSupportingDocument,
  useAPIGetTugasPokok,
  useAPIKinerjaProfile,
} from 'api/kinerja/useAPIKinerja'
import { isUserHasHybridRole } from 'app/KinerjaKSPage/utils/profile'
import { BannerGuruWithoutDefinitiveKS } from 'app/KinerjaPage/components/BannerGuruWithoutDefinitiveKS'
import CircularProgress from 'app/KinerjaPage/components/CircularProgress'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { REQUIRED_SUPPORTING_DOCUMENT_TYPES } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/supportingDocuments'
import PDFViewerDialog from 'app/KinerjaPage/pages/UploadBuktiDukung/components/FileUpload/PDFViewerDialog'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { assetUrl } from 'utils/url'
import { CardHeader } from './CardHeader'
import CardPeriodType from 'app/KinerjaShared/components/CardPeriodType'
import { useNewDocumentFlow } from 'app/KinerjaShared/hooks/useNewDocumentFlow'
import useToggleSdkContext from 'utils/useToogleSdk'
import CardPelaksanaan2026 from './CardPelaksanaan2026'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'

import type { ResponsePelaksanaanProgress } from 'types/kinerja-pelaksanaan'

export interface CardPelaksanaanNewProps {
  dataPelaksanaan: ResponsePelaksanaanProgress
  isError?: boolean
}

export default function CardPelaksanaanNew({
  dataPelaksanaan,
  isError = false,
}: CardPelaksanaanNewProps) {
  const toggles = useToggleSdkContext()
  const isAsnSwastaEnabled = toggles?.['kinerja-asn-swasta-enabled'] === true
  const { tipe } = useKinerjaTipe()

  const { status: documentFlowStatus } = useNewDocumentFlow()
  const isPelaksanaanPltKSAndGuruSameSchoolEnabled =
    process.env.NEXT_PUBLIC_SKP_PELAKSANAAN_KS_PLT_GURU_SAME_SCHOOL_ENABLED ===
    'true'

  const { periodYear } = usePeriodContext()
  const isPriorTo2026 = periodYear < 2026

  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const router = useRouter()
  const progressPercentage = dataPelaksanaan?.progressPercentage

  const { data: dataProfile, isLoading: isLoadingProfile } =
    useAPIKinerjaProfile({
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  // Get pengembangan kompetensi data
  const { data: dataPengembanganKompetensi } = useAPIGetPengembanganKompetensi({
    params: {
      tipe,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // Get tugas pokok data
  const { data: dataTugasPokok } = useAPIGetTugasPokok({
    params: { tipe },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const hasSupervisor = !!dataPelaksanaan?.supervisor
  const hasEligibleSupervisor =
    hasSupervisor &&
    (dataPelaksanaan.supervisor?.role === 'KS_DEFINITIF' ||
      dataPelaksanaan?.supervisor?.confirmationStatus === 'CONFIRMED')

  const isGuruAndKS = isUserHasHybridRole(dataProfile?.data)

  // if the user is Guru and KS PLT in the same school
  const isAlsoPltKSInTheSameSchool = dataProfile?.data?.roles?.some(
    ({ npsn, type }) => type === 'KS_PLT' && dataProfile?.data?.npsn === npsn
  )

  const satuanPendidikan = dataProfile?.data?.satuanPendidikan

  const isReadyForPelaksanaan =
    !isPelaksanaanPltKSAndGuruSameSchoolEnabled && isAlsoPltKSInTheSameSchool
      ? false
      : hasEligibleSupervisor

  const isPraktikKinerjaDone = dataPelaksanaan?.state === 'COMPLETED'
  const isPengembanganKompetensiSubmitted =
    dataPengembanganKompetensi?.data?.state === 'SUBMITTED' ||
    dataPengembanganKompetensi?.data?.state === 'APPROVED'
  const isTugasPokokSubmitted =
    dataTugasPokok?.data?.state === 'SUBMITTED' ||
    dataTugasPokok?.data?.state === 'APPROVED'

  // Completed pelaksanaan banner
  const showCompletedPelaksanaanBanner = useMemo(() => {
    // The banner will only be shown when the ASN Swasta support is released
    if (!dataPelaksanaan || !isAsnSwastaEnabled) return false

    const isPengembanganKompetensiDone =
      dataPelaksanaan?.realisasiState?.competencyImprovement === 'COMPLETED'
    const isTugasPokokDone =
      dataPelaksanaan?.realisasiState?.primaryTask === 'APPROVED'
    const isPerilakuKerjaDone = dataPelaksanaan.penilaianState === 'SUBMITTED'
    const isTugasTambahanDone =
      dataPelaksanaan.realisasiState.additionalTask === 'COMPLETED'
    const isRealisasiDone =
      dataPelaksanaan.year >= 2025
        ? isPengembanganKompetensiDone && isTugasPokokDone
        : isPengembanganKompetensiDone && isTugasTambahanDone
    const isPredikatSent = dataPelaksanaan.evaluationDocumentState === 'READY'

    if (
      isPraktikKinerjaDone &&
      isPerilakuKerjaDone &&
      isRealisasiDone &&
      !isPredikatSent
    ) {
      return true
    }

    return false
  }, [dataPelaksanaan, isAsnSwastaEnabled, isPraktikKinerjaDone])

  if (isError || !dataPelaksanaan) return <GeneralErrorSection />

  if (isLoadingProfile) {
    return (
      <div
        className="h-40 flex items-center justify-center"
        data-testid="loading-profile"
      >
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {isGuruAndKS && (
        <Text variant="heading-md">
          {`${dataProfile?.data?.satuanPendidikan} (Guru)` || 'Guru'}
        </Text>
      )}

      {(!isReadyForPelaksanaan ||
        (isAlsoPltKSInTheSameSchool &&
          isPelaksanaanPltKSAndGuruSameSchoolEnabled)) && (
        <BannerGuruWithoutDefinitiveKS
          hasPltKS={hasSupervisor}
          isAlsoPltKSInTheSameSchool={isAlsoPltKSInTheSameSchool}
          isPelaksanaanPltKSAndGuruSameSchoolEnabled={
            isPelaksanaanPltKSAndGuruSameSchoolEnabled
          }
          schoolName={satuanPendidikan}
        />
      )}

      {showCompletedPelaksanaanBanner && (
        <Card className="p-6 flex space-x-6 items-center !flex-row">
          <div className="flex-shrink-0">
            <img
              alt=""
              src={assetUrl('/illustrations/img-thumbs-up.png')}
              width={84}
              height={84}
            />
          </div>

          <div className="space-y-1">
            <Text variant="heading-lg" as="h3">
              Pelaksanaan Kinerja sudah selesai!
            </Text>
            <Text color="subdued">
              Anda dapat konfirmasi ke Kepala Sekolah dan Pejabat Penilai
              Kinerja terkait <strong>Hasil Penilaian Kinerja Anda</strong>. Cek
              lagi halaman ini nanti, ya.
            </Text>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader isLocked={!isReadyForPelaksanaan} />

        {!isPriorTo2026 && (
          <CardPelaksanaan2026
            documentFlowStatus={documentFlowStatus}
            filledBySystem={dataPelaksanaan.filledBySystem}
            isReadyForPelaksanaan={isReadyForPelaksanaan}
            isPraktikKinerjaDone={isPraktikKinerjaDone}
            isPengembanganKompetensiDone={isPengembanganKompetensiSubmitted}
            isTugasPokokDone={isTugasPokokSubmitted}
          />
        )}

        {isPriorTo2026 && isReadyForPelaksanaan && (
          <section className="px-9 pt-6 pb-9 flex flex-col gap-9 border-t border-gray-30">
            <div className="flex flex-col gap-2">
              <Text variant="heading-sm">
                {dataPelaksanaan.filledBySystem
                  ? 'Praktik Kinerja Anda telah diselesaikan oleh sistem'
                  : 'Laksanakan Praktik Kinerja:'}
              </Text>
              <div className="max-w-md flex items-center gap-6 p-6 border border-subdued rounded-lg">
                <div className="w-[84px] h-[84px]">
                  <CircularProgress
                    value={progressPercentage}
                    maxValue={100}
                    percentage
                  />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <Text variant="heading-sm">Praktik Kinerja</Text>
                  {documentFlowStatus === 'OLD_DOCUMENT_FLOW' && (
                    <Text color="subdued">
                      Periode: <CardPeriodType withYear />.
                    </Text>
                  )}
                  {!dataPelaksanaan.filledBySystem && (
                    <Button
                      onClick={() => {
                        trackEvent({
                          activity_type:
                            'pelaksanaan_click_cek_langkah_observasi',
                          screen_name: 'homepage_pengelolaan',
                        })
                        router.push(
                          `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/beranda`
                        )
                      }}
                    >
                      Cek Praktik Kinerja
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col max-w-3xl space-y-2">
              <Text variant="heading-sm">
                Laksanakan Pengembangan Kompetensi dan Tugas Pokok:
              </Text>

              <div className="grid grid-cols-2 border border-subdued rounded-lg items-start content-start gap-6 pl-6">
                <div className="py-4 pr-4 flex items-start gap-4 border-r border-subdued">
                  <img
                    src={assetUrl('/illustrations/img-document.png')}
                    alt=""
                    width={64}
                    height={64}
                  />

                  <div className="flex flex-col gap-2 items-start">
                    <Text variant="heading-sm">Pengembangan Kompetensi</Text>
                    <Button
                      disabled={documentFlowStatus === 'DISABLED'}
                      onClick={() => {
                        router.push(
                          `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/pengembangan-kompetensi`
                        )
                      }}
                    >
                      {documentFlowStatus === 'OLD_DOCUMENT_FLOW' ? (
                        <>
                          Kumpulkan
                          <span className="sr-only">
                            dokumen Pengembangan Kompetensi
                          </span>
                        </>
                      ) : documentFlowStatus === 'NEW_DOCUMENT_FLOW' ? (
                        <>
                          Lakukan
                          <span className="sr-only">
                            pengumpulan Pengembangan Kompetensi
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="sr-only">
                            Pengembangan Kompetensi
                          </span>
                          Sedang diperbarui
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="py-4 pr-4 flex items-start gap-4">
                  <img
                    src={assetUrl(
                      '/illustrations/img-badge-star-ribbon-pro.png'
                    )}
                    width={64}
                    height={64}
                    alt=""
                  />

                  <div className="flex flex-col gap-2 items-start">
                    <Text variant="heading-sm">
                      {documentFlowStatus === 'OLD_DOCUMENT_FLOW'
                        ? 'Tugas Tambahan'
                        : 'Pelaksanaan Tugas Pokok'}
                    </Text>
                    <Button
                      disabled={documentFlowStatus === 'DISABLED'}
                      onClick={() => {
                        router.push(
                          `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/${
                            documentFlowStatus === 'OLD_DOCUMENT_FLOW'
                              ? 'tugas-tambahan'
                              : 'tugas-pokok'
                          }`
                        )
                      }}
                    >
                      {documentFlowStatus === 'OLD_DOCUMENT_FLOW' ? (
                        <>
                          Kumpulkan
                          <span className="sr-only">
                            dokumen Tugas Tambahan
                          </span>
                        </>
                      ) : documentFlowStatus === 'NEW_DOCUMENT_FLOW' ? (
                        <>
                          Lakukan
                          <span className="sr-only">
                            pengumpulan Tugas Pokok
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="sr-only">Tugas Pokok</span>
                          Sedang diperbarui
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <OtherDocumentsSection />
          </section>
        )}
      </Card>
    </div>
  )
}

function OtherDocumentsSection() {
  const { status: documentFlowStatus } = useNewDocumentFlow()

  const [activeDocumentViewer, setActiveDocumentViewer] = useState<{
    fileName: string
    fileUrl: string
  }>(null)

  const { isSuccess } = useAPIKinerjaProfile({
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const {
    isFetching: isLoadingSupportingDocument,
    data: supportingDocumentData,
  } = useAPIGetSupportingDocument({
    options: {
      enabled: isSuccess,
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: false,
    },
  })

  return (
    <>
      <div className="space-y-2 max-w-3xl">
        <Text variant="heading-sm">Cek dokumen lainnya:</Text>

        <div
          className={clsx(
            documentFlowStatus === 'OLD_DOCUMENT_FLOW'
              ? 'grid grid-cols-3 items-start content-start'
              : 'inline-block',
            'border border-subdued rounded-lg p-6 gap-11'
          )}
        >
          <div className="inline-flex flex-col items-start space-y-1">
            <Text variant="body-bold">Perilaku Kerja</Text>
            <Text color="subdued">Laksanakan sesuai rencana.</Text>
            <Button
              as="a"
              href={`${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/perilaku-kerja`}
              color="white"
            >
              Cek<span className="sr-only">Perilaku Kerja</span>
            </Button>
          </div>

          {documentFlowStatus === 'OLD_DOCUMENT_FLOW' &&
            REQUIRED_SUPPORTING_DOCUMENT_TYPES.map(({ type, label }) => {
              const document =
                supportingDocumentData?.data?.documents?.find(
                  (document) => document.type === type
                ) ?? null

              const isDisabled = isLoadingSupportingDocument || !document

              return (
                <div className="flex flex-col items-start gap-1" key={type}>
                  <Text variant="body-bold">{label}</Text>
                  <Text color="subdued">Dikumpulkan oleh atasan.</Text>

                  <Button
                    color="white"
                    isLoading={isLoadingSupportingDocument}
                    disabled={isDisabled}
                    aria-haspopup={true}
                    aria-expanded={!!activeDocumentViewer}
                    onClick={() => {
                      if (!isDisabled) {
                        setActiveDocumentViewer({
                          fileName: document.fileName,
                          fileUrl: document.fileUrl,
                        })
                      }
                    }}
                  >
                    Cek<span className="sr-only">{label}</span>
                  </Button>
                </div>
              )
            })}
        </div>
      </div>
      <PDFViewerDialog
        url={activeDocumentViewer?.fileUrl}
        name={activeDocumentViewer?.fileName}
        isOpen={!!activeDocumentViewer}
        onClose={() => setActiveDocumentViewer(null)}
      />
    </>
  )
}

function GeneralErrorSection() {
  return (
    <Card>
      <section className="flex space-x-6 p-6 items-start">
        <div className="flex-shrink-0">
          <img
            alt=""
            src={assetUrl('/images/errors/img-error-fixing.png')}
            width={84}
            height={84}
          />
        </div>

        <div className="space-y-1 max-w-3xl">
          <Text variant="display-sm">Terjadi kendala sistem</Text>
          <Text color="subdued">Silakan coba lagi dalam beberapa saat.</Text>
          <Button
            size="sm"
            className="!mt-3"
            onClick={() => window.location.reload()}
          >
            Coba lagi
          </Button>
        </div>
      </section>
    </Card>
  )
}
